import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Ticket, Loader2, Check, Zap, Gift, ArrowRight } from 'lucide-react';
import { usePrizes, Prize } from '@/contexts/PrizeContext';
import { apiClient } from '@/lib/api-client';
import { formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useModalQueue } from '@/contexts/ModalQueueContext';

interface PrizePromoPopupProps {
  onClose: () => void;
}

export function PrizePromoPopup({ onClose }: PrizePromoPopupProps) {
  const navigate = useNavigate();
  const { prizes, entryTiers, purchaseEntries, getMyEntriesForPrize, loadPrizes } = usePrizes();
  const [currentPrize, setCurrentPrize] = useState<Prize | null>(null);
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entriesAwarded, setEntriesAwarded] = useState(0);
  const [newTotalEntries, setNewTotalEntries] = useState(0);
  const [scBalance, setScBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);

  // Find the first active prize
  useEffect(() => {
    const activePrizes = prizes.filter(p => p.status?.toLowerCase() === 'active');
    
    if (activePrizes.length > 0 && !currentPrize) {
      setCurrentPrize(activePrizes[0]);
      // Auto-select the first tier
      if (entryTiers.length > 0) {
        const sortedTiers = [...entryTiers].sort((a, b) => a.sc_amount - b.sc_amount);
        setSelectedTier(sortedTiers[0].id);
      }
    }
  }, [prizes, entryTiers, currentPrize]);

  useEffect(() => {
    if (currentPrize) {
      setTimeout(() => setShowAnimation(true), 50);
      fetchBalance();
    }
  }, [currentPrize]);

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await apiClient.get('/redemption/balance');
      if (response?.balance) {
        const scBonus = parseFloat(String(response.balance.sc_bonus_amount || 0)) || 0;
        const scPromo = parseFloat(String(response.balance.sc_promotional_amount || 0)) || 0;
        const scRedeemable = parseFloat(String(response.balance.sc_redeemable_amount || 0)) || 0;
        // Round to 2 decimal places to avoid floating point precision issues
        setScBalance(Math.round((scBonus + scPromo + scRedeemable) * 100) / 100);
      }
    } catch (err) {
      console.error('Failed to fetch balance:', err);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleEnter = async () => {
    if (!currentPrize || !selectedTier || loading) return;
    
    const tier = entryTiers.find(t => t.id === selectedTier);
    if (!tier) return;

    setLoading(true);
    setError(null);
    
    try {
      const result = await purchaseEntries(currentPrize.id, tier.sc_amount);
      setEntriesAwarded(result.entries);
      setNewTotalEntries(result.newTotal);
      setSuccess(true);
      fetchBalance();
      await loadPrizes();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to purchase entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onClose();
  };

  if (!currentPrize) return null;

  const myEntries = getMyEntriesForPrize(currentPrize.id);
  const selectedTierData = entryTiers.find(t => t.id === selectedTier);
  // Round tier amount to 2 decimals for consistent comparison
  const canAfford = selectedTierData ? Math.round(selectedTierData.sc_amount * 100) / 100 <= scBalance : false;
  const heroImage = currentPrize.images?.[0];

  // Calculate bonus multiplier
  const sortedTiers = [...entryTiers].sort((a, b) => a.sc_amount - b.sc_amount);
  const baseTier = sortedTiers[0];
  const baseRate = baseTier ? baseTier.entries_awarded / baseTier.sc_amount : 10;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      {/* Backdrop - clicking does NOT close */}
      <div 
        className={`absolute inset-0 bg-black/90 backdrop-blur-md transition-opacity duration-300 ${showAnimation ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* Modal */}
      <div 
        className={`relative bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] w-full max-w-sm rounded-2xl overflow-hidden border border-purple-500/30 shadow-2xl shadow-purple-500/20 transition-all duration-300 ${
          showAnimation ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        {success ? (
          /* Success State */
          <div className="p-6 text-center">
            <div className="relative mb-5">
              <div className="absolute inset-0 bg-emerald-500/30 blur-3xl rounded-full animate-pulse"></div>
              <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center mx-auto shadow-xl shadow-emerald-500/40">
                <Check className="h-10 w-10 text-white" strokeWidth={3} />
              </div>
            </div>
            <h2 className="text-2xl font-black text-white mb-2">You're Entered!</h2>
            <p className="text-white/50 text-sm mb-4">Good luck in the sweepstakes!</p>
            
            <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4">
              <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400">
                +{entriesAwarded.toLocaleString()}
              </div>
              <div className="text-emerald-400/80 text-sm font-medium">entries added</div>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-white/60 text-sm mb-5">
              <Trophy className="h-4 w-4 text-yellow-400" />
              <span>Total: <span className="text-white font-bold">{newTotalEntries.toLocaleString()}</span> entries</span>
            </div>
            
            <button
              onClick={handleDone}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white font-bold rounded-xl transition-all active:scale-[0.98]"
            >
              Done
            </button>
          </div>
        ) : (
          /* Entry Form */
          <div className="p-5">
            {/* Header with Prize Info */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-1.5 bg-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3">
                <Gift className="h-3.5 w-3.5" />
                Sweepstakes
              </div>
              <h2 className="text-xl font-black text-white mb-1">Enter for a Chance to Win!</h2>
              <p className="text-white/50 text-sm">No purchase necessary</p>
            </div>

            {/* Prize Card */}
            <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-3">
                {/* Prize Image */}
                <div className="w-16 aspect-[4/3] rounded-lg overflow-hidden bg-black/40 border border-purple-500/30 flex-shrink-0">
                  {heroImage ? (
                    <img src={heroImage} alt={currentPrize.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500/20 to-indigo-500/20">
                      <Gift className="h-6 w-6 text-purple-400/60" />
                    </div>
                  )}
                </div>
                
                {/* Prize Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-white text-sm truncate">{currentPrize.name}</h3>
                  <div className="text-yellow-400 font-black text-lg">
                    ARV ${currentPrize.estimated_value.toLocaleString()}
                  </div>
                  {myEntries > 0 && (
                    <div className="text-purple-400 text-xs">
                      You have {myEntries.toLocaleString()} entries
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Tier Selection */}
            <div className="mb-4">
              <div className="text-xs text-white/40 font-semibold uppercase tracking-wider mb-2">Select Package</div>
              <div className="grid grid-cols-2 gap-2">
                {entryTiers.slice(0, 4).map((tier) => {
                  // Round tier amount to 2 decimals for consistent comparison
                  const affordable = Math.round(tier.sc_amount * 100) / 100 <= scBalance;
                  const isSelected = selectedTier === tier.id;
                  const tierRate = tier.entries_awarded / tier.sc_amount;
                  const bonusMultiplier = Math.round(tierRate / baseRate);
                  const hasBonus = bonusMultiplier > 1;
                  
                  return (
                    <button
                      key={tier.id}
                      onClick={() => setSelectedTier(tier.id)}
                      disabled={!affordable}
                      className={`relative p-2.5 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-purple-500 bg-purple-500/20'
                          : affordable
                          ? 'border-white/10 hover:border-white/20 bg-white/5'
                          : 'border-white/5 bg-white/[0.02] opacity-40'
                      }`}
                    >
                      <div className="flex items-baseline gap-1 mb-0.5">
                        <span className="text-lg font-black text-white">{tier.entries_awarded.toLocaleString()}</span>
                        <span className="text-[9px] text-white/40">entries</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-white/60">{formatCurrency(tier.sc_amount)} SC</span>
                        {hasBonus && (
                          <span className="text-[9px] font-bold text-purple-400">{bonusMultiplier}x</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Balance */}
            <div className="flex items-center justify-between text-xs text-white/50 mb-4 px-1">
              <span>Available SC</span>
              <span className="text-emerald-400 font-semibold">
                {balanceLoading ? '...' : formatCurrency(scBalance)}
              </span>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 mb-4">
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={handleEnter}
                disabled={!canAfford || loading || !selectedTier}
                className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
                  canAfford && selectedTier
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-500/30'
                    : 'bg-white/5 text-white/30 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Zap className="h-4 w-4" />
                    {canAfford && selectedTierData ? (
                      `Get ${selectedTierData.entries_awarded.toLocaleString()} Entries`
                    ) : !selectedTier ? (
                      'Select Package'
                    ) : (
                      'Insufficient SC'
                    )}
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  onClose();
                  navigate('/prizes');
                }}
                className="w-full py-2.5 rounded-xl font-medium text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 transition-all flex items-center justify-center gap-1.5"
              >
                View Prizes
                <ArrowRight className="h-4 w-4" />
              </button>

              <button
                onClick={handleClose}
                className="w-full py-2 rounded-xl font-medium text-white/40 hover:text-white/60 transition-all text-sm"
              >
                Maybe Later
              </button>
            </div>

            {/* Footer */}
            <p className="text-[9px] text-white/30 text-center mt-3">
              No purchase necessary. See official rules.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Hook to manage showing the promo popup via modal queue
export function usePrizePromoPopup() {
  const { prizes } = usePrizes();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  const [hasChecked, setHasChecked] = useState(false);

  // Request to show via queue when there are active prizes
  useEffect(() => {
    if (hasChecked || prizes.length === 0) return;
    
    const activePrizes = prizes.filter(p => p.status?.toLowerCase() === 'active');
    
    if (activePrizes.length > 0) {
      requestShow('PRIZE_PROMO');
    }
    setHasChecked(true);
  }, [prizes, hasChecked, requestShow]);

  const closePromo = useCallback(() => {
    notifyDismissed('PRIZE_PROMO');
  }, [notifyDismissed]);

  return {
    showPromo: shouldShow('PRIZE_PROMO'),
    closePromo,
  };
}
